<script lang="ts">
	import type { CourseLightModel, CourseModel } from '@24hbs/utils';
	import { FilterStore } from '$lib/utils/filters/store';
	import type { IFilters } from '$lib/utils/filters/types';
	import { TagStore } from '$lib/utils/tag-store';
	import { onDestroy, onMount } from 'svelte';
	import { writable } from 'svelte/store';
	import { handleFilters, mergeJSON } from '$lib/utils/filters/helpers';
	import { page as pageStore } from '$app/stores';
	import {
		SectionCarousel,
		SectionCarouselIndicators
	} from '$lib/components/organisms/SectionCarousel';
	import { Section, SimpleCardsGrid } from '$lib/components/organisms/Section';
	import { Grid, ScrollableCardWrapper } from '$lib/components/organisms/Layout';
	import { CourseCard } from '$lib/components/organisms/Card';
	import { Pagination } from '$lib/components/organisms/Pagination';
	import { Filters } from '$lib/components/molecules/Filter';
	import { useSearch } from '$lib/utils/search';
	import { Search } from '$lib/utils/search/search';
	import type { ICourseCategoryFields, IListingPage, IListingPageTemplateWithImage, IListingPageTemplateWithoutImage } from '$lib/utils/contentful/types';

	export let listingPage: IListingPage;
	export let preloadCourses: CourseModel[] = [];
	export let preloadedTotalCourses: number = 0;
	export let listingPageFilters: IFilters | null = null;
	export let highlightedItems: CourseModel[] = [];

	let triggerHideStickyFilters: HTMLElement;

	const listingPageTemplate: IListingPageTemplateWithImage | IListingPageTemplateWithoutImage = listingPage.fields.template as IListingPageTemplateWithImage | IListingPageTemplateWithoutImage;

	const filterStoreInstance = FilterStore.getInstance();
	const filters = filterStoreInstance.filterStore;
	const searchStore = filterStoreInstance.searchStore;
	const isAnyFilterApplied = filterStoreInstance.isAnyFilterApplied;

	$searchStore = '';

	const { page } = $pageStore.params;
	let dataPage: number = 0;

	if (page) {
		dataPage = Number(page.replace('page-', '')) - 1;
	}

	const search = useSearch(fetch);
	const tagStoreManager = TagStore.getInstance();
	let coursesRetrieved = writable(preloadCourses);
	let totalCourses = writable(preloadedTotalCourses);
	let hasMore = false;
	let isFetching = false;
	let showBestSellers = true;
	let bestSellers = [];

	const fetchCourses = async (filters : IFilters, dataPage : number, append : boolean = true) => {
    isFetching = true;

    try {
      const { courses, total } = await search.getCourses('', filters, dataPage);
      totalCourses.set(total);
      if (append) {
        coursesRetrieved.update((currentCourses) => [...currentCourses, ...courses]);
      } else {
				tagStoreManager.initTagStore();
        coursesRetrieved.set(courses);
      }
    } catch (error) {
      coursesRetrieved.set([]);
    } finally {
      isFetching = false;
    }
  };

	const pageUnsubscriber = pageStore.subscribe((value) => {
		const filters: IFilters = mergeJSON(listingPageFilters, handleFilters($pageStore));
		filterStoreInstance.setFilters(filters);

		search.getFacets();

		fetchCourses(filters, dataPage, false);
	});

	const includeCoursesInTagCalc = (courseList: CourseModel[] | CourseLightModel[]) => {
		if (courseList && courseList.length > 0) {
			for (let course of courseList) {
				tagStoreManager.updateTagsForSession(course, false);
			}
		}
	};

	onDestroy(() => pageUnsubscriber());

	// if (courseTypeSlug && !courseCategory) {
	// 	listingPageKey = ListingPageUtils.getListingPageByListingPageSlug(listingPageSlug);
	// 	listingPageBestSellerCourses = ListingPageUtils.getInstance().listingPageBestSellerCourses;
	// }
	// $: {
	// 	bestSellers =
	// 		bestSellerCourses && bestSellerCourses.length > 0
	// 			? []
	// 			: courseTypeSlug && !courseCategory
	// 				? $listingPageBestSellerCourses.types[listingPageKey].courses
	// 				: [];
	// }

	// $: {
	// 	if (bestSellerCourses && bestSellerCourses.length > 0) {
	// 		showBestSellers = !$isAnyFilterApplied && bestSellerCourses.length > 0;

	// 		if (showBestSellers) {
	// 			includeCoursesInTagCalc(bestSellerCourses);
	// 		}
	// 	} else if (courseTypeSlug && !courseCategory) {
	// 		showBestSellers =
	// 			!$isAnyFilterApplied &&
	// 			$listingPageBestSellerCourses &&
	// 			$listingPageBestSellerCourses.types &&
	// 			$listingPageBestSellerCourses.types[listingPageKey] &&
	// 			$listingPageBestSellerCourses.types[listingPageKey].courses.length > 0;

	// 		if (showBestSellers) {
	// 			includeCoursesInTagCalc(bestSellers);
	// 		}
	// 	}
	// }

	$: hasMore = $coursesRetrieved.length < $totalCourses;
</script>

{#if listingPageTemplate.fields.showFilters }
	<Filters
		showCourseType={listingPageFilters?.courseType?.length === 0}
		showStudentType={listingPageFilters?.studentType?.length === 0 && listingPageFilters?.courseType?.length === 0}
		showCategory={listingPageFilters?.category?.length === 0}
		showLessonMode={listingPageFilters?.lessionMode?.length === 0}
		showDuration={listingPageFilters?.duration?.length === 0}
		showMonth={listingPageFilters?.month?.length === 0 && !listingPageFilters?.lessionMode?.includes('Always On')}
		showCreditsFilter={!listingPageFilters?.credits}
		{triggerHideStickyFilters}
		preloadedFilters={listingPageFilters}
	/>
{/if}

{#if highlightedItems && highlightedItems.length > 0}
	<SectionCarousel mobileCount={0} desktopCount={3} desktopBreakpoint="lg" id="section-carousel-1">
		<Section class="bg-white" compact>
			<svelte:fragment slot="header">
				<h2 class="fs-3">In evidenza</h2>
			</svelte:fragment>

			<svelte:fragment slot="indicators">
				<SectionCarouselIndicators id="section-carousel-1" />
			</svelte:fragment>

			<ScrollableCardWrapper courses={ highlightedItems } sectionCarouselId="section-carousel-1" />
		</Section>
	</SectionCarousel>
{/if}

<Section class="bg-white">
	<svelte:fragment slot="header">
		<h2 bind:this={triggerHideStickyFilters} class="fs-3">
			{listingPageTemplate.fields.productListTitle}
		</h2>
	</svelte:fragment>
	<svelte:fragment slot="filter">
		{#if !isFetching && $totalCourses > 0}
			<p class="section-results">
				{$totalCourses} risultati
			</p>
		{/if}
	</svelte:fragment>

	<Grid>
		<SimpleCardsGrid
			isInitialFetching={isFetching && $totalCourses <= 0}
			hasResult={$totalCourses > 0}
		>
			{#each $coursesRetrieved as course, index (course.idMainSession)}
				<CourseCard
					lazyLoad
					{course}
					positionInPage={(highlightedItems ? index + highlightedItems.length : (showBestSellers ? index + bestSellers.length : index) + 1)}
					isLastInList={course.slug == $coursesRetrieved[$coursesRetrieved.length - 1].slug}
				/>
			{/each}
		</SimpleCardsGrid>
	</Grid>

	<div class="mt-16" slot="footer">
		{#if hasMore}
			<Pagination
				currentPage={dataPage}
				pagesLength={Math.ceil($totalCourses / Search.DEFAULT_PAGE_SIZE)}
				listingPageSlug={listingPage.fields.slug}
			/>
		{/if}
	</div>
</Section>
